import React, { useState, useEffect } from 'react';
import apiService from '../../shared/services/ApiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../css/emailConfig.css';

const EmailConfig = () => {
    const [config, setConfig] = useState({
        resendApiKey: '',
        fromEmail: '',
        fromName: '',
        frontendUrl: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [configured, setConfigured] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);

    // Google Drive state
    const [driveConfig, setDriveConfig] = useState({ scriptUrl: '', folderId: '' });
    const [driveConfigured, setDriveConfigured] = useState(false);
    const [driveSaving, setDriveSaving] = useState(false);
    const [driveTesting, setDriveTesting] = useState(false);

    // Category images state
    const [categoryImages, setCategoryImages] = useState({});
    const [categoryList, setCategoryList] = useState([]);
    const [uploadingCategory, setUploadingCategory] = useState(null);
    const categoryFileRefs = React.useRef({});

    useEffect(() => {
        fetchConfig();
        fetchDriveConfig();
        fetchCategoryImages();
    }, []);

    const fetchConfig = async () => {
        try {
            const res = await apiService.get('/api/config/email');
            setConfig({
                resendApiKey: res.data.resendApiKey || '',
                fromEmail: res.data.fromEmail || '',
                fromName: res.data.fromName || '',
                frontendUrl: res.data.frontendUrl || ''
            });
            setConfigured(res.data.configured);
            setUpdatedAt(res.data.updatedAt);
        } catch (err) {
            toast.error('Error al cargar configuracion');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!config.resendApiKey) {
            toast.warning('El API Key de Resend es requerido');
            return;
        }
        setSaving(true);
        try {
            await apiService.put('/api/config/email', config);
            toast.success('Configuracion guardada exitosamente');
            fetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!testEmail) {
            toast.warning('Ingrese un correo de prueba');
            return;
        }
        setTesting(true);
        try {
            const res = await apiService.post('/api/config/email/test', { testEmail });
            toast.success(res.data.message);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al enviar prueba');
        } finally {
            setTesting(false);
        }
    };

    // Drive functions
    const fetchDriveConfig = async () => {
        try {
            const res = await apiService.get('/api/config/drive');
            setDriveConfig({
                scriptUrl: res.data.scriptUrl || '',
                folderId: res.data.folderId || ''
            });
            setDriveConfigured(res.data.configured);
        } catch (err) {
            console.error('Error loading drive config:', err);
        }
    };

    const handleDriveSave = async (e) => {
        e.preventDefault();
        if (!driveConfig.scriptUrl) {
            toast.warning('La URL del Apps Script es requerida');
            return;
        }
        setDriveSaving(true);
        try {
            await apiService.put('/api/config/drive', driveConfig);
            toast.success('Configuracion de Google Drive guardada');
            fetchDriveConfig();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al guardar');
        } finally {
            setDriveSaving(false);
        }
    };

    const handleDriveTest = async () => {
        setDriveTesting(true);
        try {
            const res = await apiService.post('/api/config/drive/test');
            toast.success(res.data.message);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error de conexion');
        } finally {
            setDriveTesting(false);
        }
    };

    // Category image functions
    const fetchCategoryImages = async () => {
        try {
            const res = await apiService.get('/api/category-images/admin');
            const map = {};
            (res.data.images || []).forEach(img => { map[img.category] = img; });
            setCategoryImages(map);
            setCategoryList(res.data.categories || []);
        } catch (err) {
            console.error('Error loading category images:', err);
        }
    };

    const CATEGORY_LABELS = {
        iluminacion: 'Iluminacion / Luces',
        bancos: 'Bancos / Mobiliario',
        basura: 'Basura / Limpieza',
        vegetacion: 'Vegetacion / Poda',
        infraestructura: 'Infraestructura',
        otro: 'Otro'
    };

    const handleCategoryImageUpload = async (category, file) => {
        if (!file) return;
        setUploadingCategory(category);
        try {
            const fd = new FormData();
            fd.append('image', file);
            await apiService.post(`/api/category-images/${category}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`Imagen de ${CATEGORY_LABELS[category]} actualizada`);
            fetchCategoryImages();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al subir imagen');
        } finally {
            setUploadingCategory(null);
        }
    };

    const handleCategoryImageDelete = async (category) => {
        if (!window.confirm(`Eliminar imagen de ${CATEGORY_LABELS[category]}?`)) return;
        try {
            await apiService.delete(`/api/category-images/${category}`);
            toast.success('Imagen eliminada');
            fetchCategoryImages();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al eliminar');
        }
    };

    if (loading) {
        return (
            <div className="email-config-container">
                <div className="email-config-loading">Cargando configuracion...</div>
            </div>
        );
    }

    return (
        <div className="email-config-container">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="email-config-header">
                <div className="email-config-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                    </svg>
                </div>
                <div>
                    <h2>Configuracion de Email</h2>
                    <p>Configura Resend para enviar notificaciones por email a los ciudadanos</p>
                </div>
                {configured && (
                    <span className="email-status-badge active">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Configurado
                    </span>
                )}
            </div>

            {/* Info box */}
            <div className="email-info-box">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                </svg>
                <div>
                    <strong>Como obtener el API Key de Resend (gratis):</strong>
                    <ol>
                        <li>Ve a <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer"><strong>resend.com/signup</strong></a> y crea una cuenta (puedes usar GitHub)</li>
                        <li>En el dashboard, ve a <strong>API Keys</strong></li>
                        <li>Crea una nueva API Key</li>
                        <li>Copia el key que empieza con <code>re_</code></li>
                    </ol>
                    <p style={{margin: '8px 0 0', fontSize: '12px', color: '#666'}}>
                        Plan gratis: 100 emails/dia. Mientras uses la cuenta de prueba, el remitente sera <code>onboarding@resend.dev</code>.
                    </p>
                </div>
            </div>

            <form onSubmit={handleSave} className="email-config-form">
                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                        API Key de Resend
                    </label>
                    <input
                        type="text"
                        value={config.resendApiKey}
                        onChange={e => setConfig({ ...config, resendApiKey: e.target.value })}
                        placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxx"
                        required
                    />
                    <span className="email-form-hint">Empieza con "re_". Obtenlo en resend.com/api-keys</span>
                </div>

                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        Email remitente (opcional)
                    </label>
                    <input
                        type="email"
                        value={config.fromEmail}
                        onChange={e => setConfig({ ...config, fromEmail: e.target.value })}
                        placeholder="onboarding@resend.dev"
                    />
                    <span className="email-form-hint">Dejar vacio usa onboarding@resend.dev (prueba). Para usar tu dominio, verificalo en Resend.</span>
                </div>

                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                        Nombre del remitente (opcional)
                    </label>
                    <input
                        type="text"
                        value={config.fromName}
                        onChange={e => setConfig({ ...config, fromName: e.target.value })}
                        placeholder="Parques Para Todos"
                    />
                </div>

                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        URL del Frontend (opcional)
                    </label>
                    <input
                        type="url"
                        value={config.frontendUrl}
                        onChange={e => setConfig({ ...config, frontendUrl: e.target.value })}
                        placeholder="https://developersw.com"
                    />
                    <span className="email-form-hint">URL base para los links en los emails</span>
                </div>

                <div className="email-form-actions">
                    <button type="submit" className="email-btn-save" disabled={saving}>
                        {saving ? (
                            <>Guardando...</>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                                </svg>
                                Guardar Configuracion
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Test email section */}
            {configured && (
                <div className="email-test-section">
                    <h3>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        Probar Envio
                    </h3>
                    <p>Enviar un email de prueba para verificar que todo funciona correctamente</p>
                    <div className="email-test-form">
                        <input
                            type="email"
                            value={testEmail}
                            onChange={e => setTestEmail(e.target.value)}
                            placeholder="correo-de-prueba@ejemplo.com"
                        />
                        <button onClick={handleTest} disabled={testing} className="email-btn-test">
                            {testing ? 'Enviando...' : 'Enviar Prueba'}
                        </button>
                    </div>
                    {updatedAt && (
                        <p className="email-last-updated">
                            Ultima actualizacion: {new Date(updatedAt).toLocaleString('es-ES')}
                        </p>
                    )}
                </div>
            )}

            {/* What gets sent */}
            <div className="email-what-sends">
                <h3>Que emails se envian?</h3>
                <div className="email-sends-grid">
                    <div className="email-send-card">
                        <div className="email-send-icon" style={{ background: '#e8f5e9' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <div>
                            <strong>Reporte creado</strong>
                            <p>Confirmacion con codigo de seguimiento y detalles del reporte</p>
                        </div>
                    </div>
                    <div className="email-send-card">
                        <div className="email-send-icon" style={{ background: '#e3f2fd' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0057A6" strokeWidth="2">
                                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                            </svg>
                        </div>
                        <div>
                            <strong>Cambio de estado</strong>
                            <p>Notificacion cuando el reporte cambia de estado (Revisado, Asignado, En proceso, Resuelto)</p>
                        </div>
                    </div>
                </div>
                <p className="email-note">Solo se envian emails a ciudadanos que eligieron "Email" como tipo de contacto al crear su reporte.</p>
            </div>

            {/* ========== GOOGLE DRIVE CONFIG ========== */}
            <div className="email-config-header" style={{ marginTop: '40px' }}>
                <div className="email-config-icon" style={{ background: '#e8f5e9' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                    </svg>
                </div>
                <div>
                    <h2>Google Drive - Imagenes</h2>
                    <p>Configura Google Drive para almacenar las fotos de los reportes ciudadanos</p>
                </div>
                {driveConfigured && (
                    <span className="email-status-badge active">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Configurado
                    </span>
                )}
            </div>

            <form onSubmit={handleDriveSave} className="email-config-form">
                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                        ID de la Carpeta de Google Drive
                    </label>
                    <input
                        type="text"
                        value={driveConfig.folderId}
                        onChange={e => setDriveConfig({ ...driveConfig, folderId: e.target.value })}
                        placeholder="18xHaVDKDP7yxdhO0ro6HJD2cy-RBJTDf"
                    />
                    <span className="email-form-hint">Copiar de la URL: drive.google.com/drive/folders/<strong>ESTE_ID</strong></span>
                </div>

                <div className="email-form-group">
                    <label>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        URL del Apps Script
                    </label>
                    <input
                        type="url"
                        value={driveConfig.scriptUrl}
                        onChange={e => setDriveConfig({ ...driveConfig, scriptUrl: e.target.value })}
                        placeholder="https://script.google.com/macros/s/.../exec"
                        required
                    />
                    <span className="email-form-hint">URL del Google Apps Script desplegado que maneja las subidas a Drive</span>
                </div>

                <div className="email-form-actions">
                    <button type="submit" className="email-btn-save" disabled={driveSaving}>
                        {driveSaving ? 'Guardando...' : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                                </svg>
                                Guardar Configuracion
                            </>
                        )}
                    </button>
                </div>
            </form>

            {driveConfigured && (
                <div className="email-test-section">
                    <h3>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        Probar Conexion
                    </h3>
                    <p>Sube un archivo de prueba para verificar la conexion con Google Drive</p>
                    <button onClick={handleDriveTest} disabled={driveTesting} className="email-btn-test">
                        {driveTesting ? 'Probando...' : 'Probar Conexion'}
                    </button>
                </div>
            )}

            {/* ========== CATEGORY IMAGES ========== */}
            <div className="email-config-header" style={{ marginTop: '40px' }}>
                <div className="email-config-icon" style={{ background: '#fff3e0' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                    </svg>
                </div>
                <div>
                    <h2>Imagenes por Categoria</h2>
                    <p>Sube una imagen de referencia para cada categoria de reporte</p>
                </div>
            </div>

            {!driveConfigured && (
                <div className="email-info-box" style={{ borderLeft: '4px solid #f44336' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f44336" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <div>
                        <strong>Google Drive no configurado</strong>
                        <p>Configure Google Drive arriba antes de subir imagenes de categoria.</p>
                    </div>
                </div>
            )}

            <div className="category-images-grid">
                {(categoryList.length > 0 ? categoryList : ['iluminacion', 'bancos', 'basura', 'vegetacion', 'infraestructura', 'otro']).map(cat => (
                    <div key={cat} className="category-image-card">
                        <div className="category-image-preview">
                            {categoryImages[cat] ? (
                                <img
                                    src={categoryImages[cat].thumbnailLink || categoryImages[cat].directLink}
                                    alt={CATEGORY_LABELS[cat]}
                                    loading="lazy"
                                />
                            ) : (
                                <div className="category-image-empty">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                        <polyline points="21 15 16 10 5 21"/>
                                    </svg>
                                    <span>Sin imagen</span>
                                </div>
                            )}
                        </div>
                        <div className="category-image-info">
                            <strong>{CATEGORY_LABELS[cat] || cat}</strong>
                            {categoryImages[cat] && (
                                <span className="category-image-file">{categoryImages[cat].originalName}</span>
                            )}
                        </div>
                        <div className="category-image-actions">
                            <button
                                type="button"
                                className="category-upload-btn"
                                disabled={!driveConfigured || uploadingCategory === cat}
                                onClick={() => categoryFileRefs.current[cat]?.click()}
                            >
                                {uploadingCategory === cat ? 'Subiendo...' : (categoryImages[cat] ? 'Cambiar' : 'Subir')}
                            </button>
                            {categoryImages[cat] && (
                                <button
                                    type="button"
                                    className="category-delete-btn"
                                    onClick={() => handleCategoryImageDelete(cat)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    </svg>
                                </button>
                            )}
                            <input
                                type="file"
                                ref={el => categoryFileRefs.current[cat] = el}
                                accept="image/jpeg,image/png,image/webp"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    if (e.target.files[0]) handleCategoryImageUpload(cat, e.target.files[0]);
                                    e.target.value = '';
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EmailConfig;
